/** slaIndicator — utilitario compartido para indicar el estado del SLA de un ticket.
 *
 * Semántica acordada con producto:
 *   - SLA = horas reloj continuas que SOLO pausan en días no hábiles completos
 *     (sábados, domingos, feriados PE). El backend hace el cálculo y expone
 *     `ticket.sla_segs_restantes`:
 *       > 0   → falta tiempo (positivo = segundos efectivos restantes)
 *       < 0   → vencido (negativo = segundos efectivos ya pasados)
 *       null  → sin SLA o ticket TERMINADO
 *
 * Colores:
 *   - Verde:     > 8h hábiles restantes
 *   - Amarillo:  0 – 8h hábiles restantes (Por vencer)
 *   - Rojo:      vencido
 *   - Gris:      ticket TERMINADO (muestra "cumplido"/"incumplido" según sla_cumplido)
 *   - Sin SLA:   gris claro
 */
const STATUS = {
  OK:        { color: 'text-emerald-700', bg: 'bg-emerald-100', dot: 'bg-emerald-500', label: 'En tiempo' },
  WARN:      { color: 'text-amber-800',   bg: 'bg-amber-100',   dot: 'bg-amber-500',   label: 'Por vencer' },
  VENCIDO:   { color: 'text-rose-800',    bg: 'bg-rose-100',    dot: 'bg-rose-500',    label: 'Vencido' },
  CUMPLIDO:  { color: 'text-emerald-700', bg: 'bg-emerald-50',  dot: 'bg-emerald-400', label: 'SLA cumplido' },
  INCUMPLIDO:{ color: 'text-rose-800',    bg: 'bg-rose-50',     dot: 'bg-rose-400',    label: 'SLA incumplido' },
};

const UMBRAL_WARN_SEGS = 8 * 3600;  // 8h hábiles para entrar en estado amarillo

/** Formatea segundos efectivos a "Xd Yh", "Xh Ym" o "<1m" según rango. */
function formatSegs(segs) {
  const abs = Math.abs(segs);
  if (abs < 60) return '<1m';
  const dias = Math.floor(abs / 86400);
  const horas = Math.floor((abs % 86400) / 3600);
  const minutos = Math.floor((abs % 3600) / 60);
  if (dias > 0) {
    return horas > 0 ? `${dias}d ${horas}h` : `${dias}d`;
  }
  if (horas > 0) {
    return minutos > 0 && horas < 4 ? `${horas}h ${minutos}m` : `${horas}h`;
  }
  return `${minutos}m`;
}

export function getSlaState(ticket) {
  if (!ticket) return null;
  if (ticket.estado === 'TERMINADO') {
    if (ticket.sla_cumplido === true) return { ...STATUS.CUMPLIDO, key: 'CUMPLIDO' };
    if (ticket.sla_cumplido === false) return { ...STATUS.INCUMPLIDO, key: 'INCUMPLIDO' };
    return null;
  }
  if (!ticket.fecha_vencimiento_sla) {
    return { color: 'text-slate-500', bg: 'bg-slate-100', dot: 'bg-slate-300', label: 'Sin SLA', key: 'NONE' };
  }

  const segs = typeof ticket.sla_segs_restantes === 'number' ? ticket.sla_segs_restantes : null;

  if (segs === null) {
    // Fallback: usar diferencia plana si el backend no envió el campo computed
    return { color: 'text-slate-500', bg: 'bg-slate-100', dot: 'bg-slate-300', label: 'Sin SLA', key: 'NONE' };
  }

  if (segs <= 0) {
    return { ...STATUS.VENCIDO, key: 'VENCIDO', detalle: `${formatSegs(segs)} vencido` };
  }
  if (segs <= UMBRAL_WARN_SEGS) {
    return { ...STATUS.WARN, key: 'WARN', detalle: `${formatSegs(segs)} restantes` };
  }
  return { ...STATUS.OK, key: 'OK', detalle: `${formatSegs(segs)} restantes` };
}

export function formatVencimiento(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
}
